import { useMemo } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { FormattedMessage, useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import FormikTextField from '../../../../shared/components/formik/FormikTextField'
import FormikSelectField from '../../../../shared/components/formik/FormikSelectField'
import FormikCheckboxField from '../../../../shared/components/formik/FormikCheckboxField'
import FormikNumericField from '../../../../shared/components/formik/FormikNumericField'
import FormikArrayField from '../../../../shared/components/formik/FormikArrayField'

const ServiceEditForm = ({ service, updateService, onSuccess, onCancel }) => {
  const intl = useIntl()

  const expectedStateOptions = useMemo(() => {
    return [
      { value: 'running', label: intl.formatMessage({ id: 'dockerServices.expectedStates.running' }) },
      { value: 'stopped', label: intl.formatMessage({ id: 'dockerServices.expectedStates.stopped' }) }
    ]
  }, [intl])

  const restartPolicyOptions = useMemo(() => {
    return [
      { value: '', label: '' },
      { value: 'no', label: intl.formatMessage({ id: 'dockerServices.restartPolicies.no' }) },
      { value: 'always', label: intl.formatMessage({ id: 'dockerServices.restartPolicies.always' }) },
      { value: 'on-failure', label: intl.formatMessage({ id: 'dockerServices.restartPolicies.onFailure' }) },
      { value: 'unless-stopped', label: intl.formatMessage({ id: 'dockerServices.restartPolicies.unlessStopped' }) }
    ]
  }, [intl])

  const platformOptions = useMemo(() => {
    return [
      { value: '', label: intl.formatMessage({ id: 'dockerServices.platforms.default' }) },
      { value: 'linux/amd64', label: 'linux/amd64' },
      { value: 'linux/arm64', label: 'linux/arm64' },
      { value: 'linux/arm/v7', label: 'linux/arm/v7' },
      { value: 'linux/arm/v6', label: 'linux/arm/v6' },
      { value: 'linux/386', label: 'linux/386' }
    ]
  }, [intl])

  const validationSchema = Yup.object({
    name: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    image: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    expectedState: Yup.string(),
    logStreaming: Yup.boolean(),
    restartPolicy: Yup.string(),
    restartMaxRetries: Yup.number().nullable(),
    command: Yup.array().of(Yup.string()),
    containerName: Yup.string(),
    platform: Yup.string()
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: service?.name || '',
      image: service?.image || '',
      expectedState: service?.expectedState || 'running',
      logStreaming: service?.logStreaming || false,
      restartPolicy: service?.restartPolicy || '',
      restartMaxRetries: service?.restartMaxRetries || null,
      command: service?.command || [],
      containerName: service?.containerName || '',
      platform: service?.platform || ''
    },
    validationSchema,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      const serviceData = {
        service: {
          name: values.name,
          image: values.image,
          expectedState: values.expectedState,
          logStreaming: values.logStreaming,
          restartPolicy: values.restartPolicy || null,
          restartMaxRetries: values.restartPolicy === 'on-failure' ? values.restartMaxRetries : null,
          command: values.command.length > 0 ? values.command.filter(c => c) : null,
          containerName: values.containerName || null,
          platform: values.platform || null
        }
      }

      updateService(service.slug, serviceData)
        .then(() => {
          onSuccess()
        })
        .catch((err) => {
          setStatus(err.message || 'Error updating service')
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  })

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <FormikTextField
        formik={formik}
        name="name"
        label={<FormattedMessage id="dockerServices.create.nameLabel" />}
        autoFocus
      />

      <Box sx={{ mt: 2 }}>
        <FormikTextField
          formik={formik}
          name="image"
          label={<FormattedMessage id="dockerServices.create.imageLabel" />}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormikSelectField
          formik={formik}
          name="expectedState"
          label={<FormattedMessage id="dockerServices.create.expectedStateLabel" />}
          options={expectedStateOptions}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormikCheckboxField
          formik={formik}
          name="logStreaming"
          label={<FormattedMessage id="dockerServices.create.logStreamingLabel" />}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormikSelectField
          formik={formik}
          name="restartPolicy"
          label={<FormattedMessage id="dockerServices.create.restartPolicyLabel" />}
          options={restartPolicyOptions}
        />
      </Box>

      {formik.values.restartPolicy === 'on-failure' && (
        <Box sx={{ mt: 2 }}>
          <FormikNumericField
            formik={formik}
            name="restartMaxRetries"
            label={<FormattedMessage id="dockerServices.create.restartMaxRetriesLabel" />}
          />
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        <FormikArrayField
          formik={formik}
          name="command"
          label={<FormattedMessage id="dockerServices.create.commandLabel" />}
          addLabel={<FormattedMessage id="dockerServices.create.addCommand" />}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormikTextField
          formik={formik}
          name="containerName"
          label={<FormattedMessage id="dockerServices.create.containerNameLabel" />}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormikSelectField
          formik={formik}
          name="platform"
          label={<FormattedMessage id="dockerServices.create.platformLabel" />}
          options={platformOptions}
        />
      </Box>

      {formik.status && (
        <Typography color="error" sx={{ mt: 2 }}>
          {formik.status}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={formik.isSubmitting}
        >
          <FormattedMessage id="app.actions.cancel.label" />
        </Button>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={formik.isSubmitting}
        >
          <FormattedMessage id="dockerServices.edit.submitButton" />
        </Button>
      </Box>
    </Box>
  )
}

export default ServiceEditForm
